import { getToken } from "@/helpers/localStorage";
import ky, { HTTPError } from "ky";
import { captureException, withScope } from "@sentry/react";

interface ErrorResponse extends HTTPError {
  data?: unknown;
}

export type Api = typeof ky;

export const api = ky.create({
  prefixUrl: import.meta.env.VITE_API_BOT_URL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: false,
  hooks: {
    beforeRequest: [
      (request) => {
        const token = getToken();
        if (token) {
          request.headers.set("Authorization", `Bearer ${token}`);
        }
      },
    ],
    afterResponse: [
      async (_request, _options, response) => {
        if (response.status !== 200) {
          return response;
        }
        const data = await response.json();
        return new Response(JSON.stringify(data), response);
      },
    ],
    beforeError: [
      async (error: ErrorResponse) => {
        if (error.response) {
          const clonedResponse = error.response.clone(); // Clone the response as the body stream can only be read once
          try {
            const errorData = await error.response.json();
            error.data = errorData;

            // Capture API errors in Sentry
            withScope((scope) => {
              scope.setTag("type", "bot-api-error");
              scope.setExtra("url", error.response.url);
              scope.setExtra("status", error.response.status);
              scope.setExtra("method", error.response.statusText);
              scope.setExtra("data", errorData);

              captureException(error);
            });
          } catch {
            // Due to the inconsistent response format from the bot API, we have to handle cases where plain text is returned instead of JSON
            try {
              const errorData = await clonedResponse.text();
              error.data = errorData;

              // Capture text response errors in Sentry
              withScope((scope) => {
                scope.setTag("type", "bot-api-error");
                scope.setExtra("url", error.response.url);
                scope.setExtra("status", error.response.status);
                scope.setExtra("method", error.response.statusText);
                scope.setExtra("data", errorData);

                captureException(error);
              });
            } catch {
              // If both JSON and text parsing fail, still capture
              withScope((scope) => {
                scope.setTag("type", "bot-api-error");
                scope.setExtra("url", error.response.url);
                scope.setExtra("status", error.response.status);
                scope.setExtra("method", error.response.statusText);

                captureException(error);
              });
            }
          }
        } else {
          // Network errors (timeout, DNS, etc.)
          withScope((scope) => {
            scope.setTag("type", "bot-api-network-error");
            captureException(error);
          });
        }

        return error;
      },
    ],
  },
});
